/**
 * Issue #851 (Lifecycle v2 Stage 5): dual freshness indicator.
 *
 * Two orthogonal dimensions (design #846):
 * - release status — can listeners get it? (published / scheduled / wip)
 * - pipeline front — is the machine done with it? (front_step: the first
 *   pipeline step with incomplete produces; null = artifact-complete)
 *
 * Anything not at its final artifact renders on a warning background — that
 * covers both first-time generation in progress and (post-#850b) mid-wave
 * staleness.
 */

import { Chip, Stack, Tooltip } from '@mui/material';

// Short display labels for pipeline step keys (= entry function names, #847)
export const STEP_LABELS: Record<string, string> = {
  'gen-story-outline': 'Outline',
  'gen-story-embedding': 'Embedding',
  'gen-story-image': 'Image',
  'gen-story-announce': 'Announce',
  'gen-story-script': 'Script',
  'gen-story-voice': 'Voice',
  'gen-story-mixed-audio': 'Audio mix',
  'gen-story-mixed-audio-external': 'External mix',
  'story-pre-publish': 'Pre-publish',
};

export function stepLabel(stepKey: string): string {
  return STEP_LABELS[stepKey] ?? stepKey;
}

interface FreshnessBadgeProps {
  /** Pipeline front (null/undefined = complete or not yet computed) */
  frontStep?: string | null;
  /** published | scheduled | null (wip) */
  releaseStatus?: string | null;
  /** Hide the release chip (story-level rows have no release dimension) */
  hideRelease?: boolean;
}

const releaseChip = (releaseStatus?: string | null) => {
  if (releaseStatus === 'published') {
    return <Chip label="🔥 Published" size="small" color="error" variant="filled" />;
  }
  if (releaseStatus === 'scheduled') {
    return <Chip label="🕓 Scheduled" size="small" color="success" variant="filled" />;
  }
  return <Chip label="WIP" size="small" variant="outlined" />;
};

export const FreshnessBadge = ({ frontStep, releaseStatus, hideRelease }: FreshnessBadgeProps) => (
  <Stack direction="row" spacing={0.5} alignItems="center">
    {!hideRelease && releaseChip(releaseStatus)}
    {frontStep && (
      <Tooltip title={`Pipeline is not at its final artifact — next: ${stepLabel(frontStep)}`}>
        <Chip
          label={`🔄 ${stepLabel(frontStep)}`}
          size="small"
          color="warning"
          variant="outlined"
        />
      </Tooltip>
    )}
  </Stack>
);

FreshnessBadge.displayName = 'FreshnessBadge';
